import { useState, useMemo } from 'react';
import { SessionMetadata } from '../types';
import { Search, Plus, FileText } from 'lucide-react';
import SessionCard from './SessionCard';

interface Props {
  sessions: SessionMetadata[];
  currentSessionId: string;
  onSelectSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  onNewSession: () => void;
}

interface SessionGroup {
  label: string;
  sessions: SessionMetadata[];
}

function groupSessionsByTime(sessions: SessionMetadata[]): SessionGroup[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const thisWeekStart = new Date(today.getTime() - today.getDay() * 24 * 60 * 60 * 1000);

  const groups: Record<string, SessionMetadata[]> = {
    Today: [],
    Yesterday: [],
    'This Week': [],
    Older: [],
  };

  sessions.forEach((session) => {
    const sessionDate = new Date(session.updatedAt);
    if (sessionDate >= today) {
      groups['Today'].push(session);
    } else if (sessionDate >= yesterday) {
      groups['Yesterday'].push(session);
    } else if (sessionDate >= thisWeekStart) {
      groups['This Week'].push(session);
    } else {
      groups['Older'].push(session);
    }
  });

  return Object.entries(groups)
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({
      label,
      sessions: items.sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      ),
    }));
}

export default function SessionList({
  sessions,
  currentSessionId,
  onSelectSession,
  onDeleteSession,
  onNewSession,
}: Props) {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredSessions = useMemo(() => {
    if (!searchTerm.trim()) return sessions;
    const lower = searchTerm.toLowerCase();
    return sessions.filter(
      (s) =>
        s.name.toLowerCase().includes(lower) ||
        s.documentType.toLowerCase().includes(lower) ||
        (s.preview && s.preview.toLowerCase().includes(lower))
    );
  }, [sessions, searchTerm]);

  const groupedSessions = useMemo(
    () => groupSessionsByTime(filteredSessions),
    [filteredSessions]
  );

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-3 border-b border-gray-100">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search sessions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-9 text-sm w-full"
          />
        </div>
      </div>

      {/* New Session Button */}
      <div className="p-3 border-b border-gray-100">
        <button
          onClick={onNewSession}
          className="flex items-center gap-2 w-full px-3 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent-dark transition-colors"
        >
          <Plus size={16} />
          New Session
        </button>
      </div>

      {/* Sessions List */}
      <div className="flex-1 overflow-y-auto">
        {filteredSessions.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FileText size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">
              {searchTerm ? 'No sessions match your search' : 'No sessions yet'}
            </p>
          </div>
        ) : (
          <div className="p-2 space-y-4">
            {groupedSessions.map((group) => (
              <div key={group.label}>
                <h4 className="px-2 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {group.label}
                </h4>
                <div className="space-y-1 mt-1">
                  {group.sessions.map((session) => (
                    <SessionCard
                      key={session.id}
                      session={session}
                      isActive={session.id === currentSessionId}
                      onClick={() => onSelectSession(session.id)}
                      onDelete={() => onDeleteSession(session.id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
